'use client';

import { useCallback } from 'react';
import { useRouter } from 'next/navigation';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark, faCrown, faArrowRight, faArrowUpRightFromSquare } from '@fortawesome/free-solid-svg-icons';
import clsx from 'clsx';
import { useUser } from '@/context/UserContext';

/**
 * URL de assinatura do CrieOferta.
 * Redireciona o usuário para a página de planos do CrieOferta.
 */
// Base URL do CrieOferta (override por NEXT_PUBLIC_CRIE_OFERTA_URL).
// Em dev usar http://127.0.0.1:8000; em prod manter https://crieoferta.com.
const CRIE_OFERTA_BASE_URL =
  process.env.NEXT_PUBLIC_CRIE_OFERTA_URL || 'https://crieoferta.com';

/**
 * Monta a URL de assinatura do CrieOferta incluindo `returnTo` para que o
 * fluxo retorne a esta mesma pagina apos a assinatura ser concluida.
 */
const buildCrieOfertaSubscriptionUrl = (artworkId?: string | number | null): string => {
  const base = CRIE_OFERTA_BASE_URL.replace(/\/$/, '');
  if (typeof window === 'undefined') return `${base}/assinatura`;
  // Quando temos artworkId, returnTo aponta para /arte/<id> para que o
  // ArtworkModal seja reaberto automaticamente apos a assinatura.
  const returnUrl = artworkId
    ? new URL(`/arte/${artworkId}`, window.location.origin).toString()
    : window.location.href;
  return `${base}/assinatura?returnTo=${encodeURIComponent(returnUrl)}`;
};

interface SubscriptionRequiredModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Sobrescrever a origem (útil quando vem de resposta 403 do backend) */
  forceOrigin?: 'crie_oferta' | 'direct' | null;
  /** Quando informado, o fluxo SSO retorna para /arte/<id> e reabre o modal. */
  artworkId?: string | number | null;
}

/**
 * Modal que aparece quando um usuário não assinante tenta acessar recurso premium.
 * 
 * Comportamento baseado na origem:
 * - CrieOferta → redireciona para página de assinatura do CrieOferta
 * - Direto → redireciona para /planos local do Artes Turbo
 */
export default function SubscriptionRequiredModal({
  isOpen,
  onClose,
  forceOrigin,
  artworkId
}: SubscriptionRequiredModalProps) {
  const router = useRouter();
  const { user } = useUser();

  const origin = forceOrigin ?? user?.registrationSource ?? 'direct';
  const isCrieOferta = origin === 'crie_oferta';

  const handleSubscribe = useCallback(() => {
    onClose();
    if (isCrieOferta) {
      // Redirecionar para página de assinatura do CrieOferta
      window.location.href = buildCrieOfertaSubscriptionUrl(artworkId ?? null);
    } else {
      // Redirecionar para página de planos local
      router.push('/planos');
    }
  }, [isCrieOferta, onClose, router, artworkId]);

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/60 z-[9998] animate-in fade-in duration-200"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
        <div 
          className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full animate-in zoom-in-95 duration-200"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Close button */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 transition-colors"
          >
            <FontAwesomeIcon icon={faXmark} className="w-4 h-4 text-gray-600" />
          </button>

          {/* Content */}
          <div className="p-8 text-center">
            {/* Icon */}
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br from-purple-500/20 to-indigo-500/20 mb-6">
              <FontAwesomeIcon icon={faCrown} className="w-8 h-8 text-purple-600" />
            </div>

            {/* Title */}
            <h2 className="text-2xl font-bold text-gray-900 mb-3">
              Recurso Premium
            </h2>

            {/* Message */}
            <p className="text-gray-600 mb-6">
              {isCrieOferta ? (
                <>
                  Para acessar este recurso, é necessário ter uma assinatura ativa no <strong>Crie Oferta</strong>. 
                  Acesse seu painel para assinar ou renovar seu plano.
                </>
              ) : (
                <>
                  Este recurso está disponível apenas para assinantes premium.
                  Assine agora e tenha acesso a todo o acervo!
                </>
              )}
            </p>

            {/* Info box */}
            <div className={clsx(
              "rounded-xl p-4 mb-6 border",
              isCrieOferta
                ? "bg-orange-50 border-orange-200"
                : "bg-blue-50 border-blue-200"
            )}>
              <p className={clsx(
                "text-sm font-medium",
                isCrieOferta ? "text-orange-800" : "text-blue-800"
              )}>
                {isCrieOferta
                  ? "🔗 Sua conta está vinculada ao Crie Oferta. A assinatura é gerenciada por lá."
                  : "💡 Assine e desbloqueie downloads ilimitados, editor avançado e muito mais!"
                }
              </p>
            </div>

            {/* Actions */}
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={onClose}
                className="flex-1 px-6 py-3 rounded-xl border-2 border-gray-300 text-gray-700 font-semibold hover:bg-gray-50 transition-colors"
              >
                Fechar
              </button>
              <button
                onClick={handleSubscribe}
                className={clsx(
                  "flex-1 px-6 py-3 rounded-xl font-semibold transition-all",
                  "shadow-lg hover:shadow-xl",
                  "flex items-center justify-center gap-2",
                  isCrieOferta
                    ? "bg-gradient-to-r from-orange-500 to-orange-600 text-white hover:from-orange-600 hover:to-orange-700"
                    : "bg-gradient-to-r from-purple-500 to-indigo-600 text-white hover:from-purple-600 hover:to-indigo-700"
                )}
              >
                {isCrieOferta ? 'Assinar no Crie Oferta' : 'Ver Planos'}
                <FontAwesomeIcon 
                  icon={isCrieOferta ? faArrowUpRightFromSquare : faArrowRight} 
                  className="w-4 h-4" 
                />
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
